import { create } from 'zustand';
import {
  CalendarViewType,
  ViewMode,
  shouldRestorePersistedDate,
} from '../utils/enums';
import { logger } from '../../../../../lib/logger';
import { Calendar, Resource } from '../components/types';
import { persist } from 'zustand/middleware';

export interface SavedFilterPreset {
  id: string;
  value: string;
  createdAt: number;
}

interface CalendarViewState {
  viewType: CalendarViewType;
  viewMode: ViewMode;
  selectedCalendarIds: string[];
  selectedResourceIds: string[];
  dateRange: {
    start: string;
    end: string;
    /** FullCalendar view.currentStart — the interval being viewed (e.g. 1st of month), not the visible grid spillover. */
    currentStart?: string;
    timestamp: number;
  } | null;
  titleFilter: string | null;
  savedFilterPresets: SavedFilterPreset[];
  setViewType: (viewType: CalendarViewType) => void;
  setViewMode: (viewMode: ViewMode) => void;
  setSelectedCalendarIds: (ids: string[]) => void;
  setSelectedResourceIds: (ids: string[]) => void;
  setDateRange: (
    range: { start: Date; end: Date; currentStart: Date } | null,
  ) => void;
  setTitleFilter: (value: string | null) => void;
  saveFilterPreset: (value: string) => SavedFilterPreset | null;
  applyFilterPreset: (id: string) => void;
  deleteFilterPreset: (id: string) => void;
  initializeSelections: (
    calendars?: Calendar[],
    resources?: Resource[],
  ) => void;
  reset: () => void;
}

// Revisit policy (localStorage via zustand persist):
// - viewMode / viewType / selections / filters: restored indefinitely
// - week/day date position: always today (dateRange cleared on load)
// - month date position: restored for 24h, then today
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export const useCalendarViewStore = create<CalendarViewState>()(
  persist(
    (set) => ({
      viewType: CalendarViewType.CALENDARS,
      viewMode: ViewMode.TIME_GRID_WEEK,
      selectedCalendarIds: [],
      selectedResourceIds: [],
      dateRange: null,
      titleFilter: null,
      savedFilterPresets: [],

      setViewType: (type) => set((state) => ({ ...state, viewType: type })),

      setViewMode: (newViewMode) =>
        set((state) => ({ ...state, viewMode: newViewMode })),

      setDateRange: (range) =>
        set((state) => ({
          ...state,
          dateRange: range
            ? {
                start: range.start.toISOString(),
                end: range.end.toISOString(),
                currentStart: range.currentStart.toISOString(),
                timestamp: Date.now(),
              }
            : null,
        })),

      setSelectedCalendarIds: (ids) => {
        logger.debug('📅 Selected Calendars Changed:', { ids });
        set({ selectedCalendarIds: ids });
      },

      setSelectedResourceIds: (ids) => {
        logger.debug('📅 Selected Resources Changed:', { ids });
        set({ selectedResourceIds: ids });
      },

      setTitleFilter: (value) => {
        // Normalize: trim and treat empty string as null so skip-logic and
        // change detection work consistently.
        const next = value && value.trim().length > 0 ? value : null;
        logger.debug('📅 Title Filter Changed:', { value: next });
        set({ titleFilter: next });
      },

      saveFilterPreset: (value) => {
        const trimmedValue = value.trim();
        if (!trimmedValue) return null;
        const preset: SavedFilterPreset = {
          id: `preset_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          value: trimmedValue,
          createdAt: Date.now(),
        };
        set((state) => ({
          savedFilterPresets: [...state.savedFilterPresets, preset],
        }));
        return preset;
      },

      applyFilterPreset: (id) => {
        set((state) => {
          const preset = state.savedFilterPresets.find((p) => p.id === id);
          if (!preset) return state;
          return { ...state, titleFilter: preset.value || null };
        });
      },

      deleteFilterPreset: (id) => {
        set((state) => ({
          savedFilterPresets: state.savedFilterPresets.filter(
            (p) => p.id !== id,
          ),
        }));
      },

      initializeSelections: (calendars, resources) => {
        set((state) => {
          const updates: Partial<CalendarViewState> = {};

          // Only clear expired ranges here. Week/day "always today" is handled
          // once in persist merge on rehydrate — clearing again races with
          // datesSet and wipes the header title after calendars load.
          if (state.dateRange) {
            const isExpired =
              Date.now() - state.dateRange.timestamp > TWENTY_FOUR_HOURS_MS;
            if (isExpired) {
              logger.debug('📅 Date range expired, clearing selection');
              updates.dateRange = null;
            } else if (
              shouldRestorePersistedDate(state.viewMode) &&
              !state.dateRange.currentStart
            ) {
              // Backfill currentStart for pre-upgrade persisted month ranges
              // (active start can be in the prior month).
              const start = new Date(state.dateRange.start).getTime();
              const end = new Date(state.dateRange.end).getTime();
              updates.dateRange = {
                ...state.dateRange,
                currentStart: new Date(start + (end - start) / 2).toISOString(),
              };
            }
          }

          if (calendars?.length) {
            const selectedIds = state.selectedCalendarIds;
            const validCalendarIds = selectedIds.filter((id) =>
              calendars.some((calendar) => calendar.id === id),
            );

            if (validCalendarIds.length !== selectedIds.length) {
              logger.debug('📅 Filtering out non-existent calendars:', {
                invalidIds: selectedIds.filter(
                  (id) => !validCalendarIds.includes(id),
                ),
              });
              updates.selectedCalendarIds = validCalendarIds;
            }

            if (validCalendarIds.length === 0) {
              const primaryCalendar = calendars.find(
                (calendar) => calendar.isPrimary,
              );
              if (primaryCalendar) {
                logger.debug('📅 Auto-selecting primary calendar:', {
                  calendarId: primaryCalendar.id,
                });
                updates.selectedCalendarIds = [primaryCalendar.id];
              }
            }
          }

          if (resources?.length) {
            const selectedIds = state.selectedResourceIds;
            const validResourceIds = selectedIds.filter((id) =>
              resources.some((resource) => resource.id === id),
            );

            if (validResourceIds.length !== selectedIds.length) {
              logger.debug('📅 Filtering out non-existent resources:', {
                invalidIds: selectedIds.filter(
                  (id) => !validResourceIds.includes(id),
                ),
              });
              updates.selectedResourceIds = validResourceIds;
            }

            if (validResourceIds.length === 0) {
              const firstThreeResources = resources.slice(0, 3);
              if (firstThreeResources.length) {
                logger.debug('📅 Auto-selecting first three resources:', {
                  resourceIds: firstThreeResources.map(
                    (resource) => resource.id,
                  ),
                });
                updates.selectedResourceIds = firstThreeResources.map(
                  (resource) => resource.id,
                );
              }
            }
          }

          return updates;
        });
      },

      reset: () => {
        set({
          viewType: CalendarViewType.CALENDARS,
          viewMode: ViewMode.TIME_GRID_WEEK,
          selectedCalendarIds: [],
          selectedResourceIds: [],
          dateRange: null,
          titleFilter: null,
          savedFilterPresets: [],
        });
      },
    }),
    {
      name: 'calendar-view-store',
      partialize: (state) => ({
        viewType: state.viewType,
        viewMode: state.viewMode,
        selectedCalendarIds: state.selectedCalendarIds,
        selectedResourceIds: state.selectedResourceIds,
        dateRange: state.dateRange,
        titleFilter: state.titleFilter,
        savedFilterPresets: state.savedFilterPresets,
      }),
      merge: (persisted, current) => {
        const persistedState = (persisted ?? {}) as Partial<CalendarViewState>;
        const merged = { ...current, ...persistedState };

        if (merged.dateRange) {
          const isExpired =
            Date.now() - merged.dateRange.timestamp > TWENTY_FOUR_HOURS_MS;
          if (isExpired || !shouldRestorePersistedDate(merged.viewMode)) {
            merged.dateRange = null;
          }
        }

        return merged;
      },
    },
  ),
);
